import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { anime } from 'react-anime';
import { useRoom, roomStatuses } from '../../contexts/RoomProvider'
import { Button, Box, Typography, Grid, Dialog, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, SnackbarContent, Divider, useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { format } from 'date-fns'
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SettingsIcon from '@material-ui/icons/Settings'
import HistoryIcon from '@material-ui/icons/History'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import GroupIcon from '@material-ui/icons/Group'
import GroupAddIcon from '@material-ui/icons/GroupAdd'
import ShareIcon from '@material-ui/icons/Share'
import EmoteIcon from '@material-ui/icons/InsertEmoticon'
import TimerIcon from "@material-ui/icons/Timer"
import FileCopyIcon from "@material-ui/icons/FileCopy"
import PokerCard from './PokerCard'
import CardPicker from './CardPicker';
import ResultsChart from './ResultsChart';
import CreateDialogContent from '../join/CreateDialogContent';
import { Estimate, GameType, RoomProperties, RoomState, RoomStatus } from '../../models/ServerModels';
import { notifyCardsFlipped } from '../../util/util'
import EventHistoryDialogContent from './EventHistoryDialogContent';
import FreeResponseInput from './FreeResponseInput';
import SetTimerDialogContent from './SetTimerDialogContent';
import ExportResultsDialogContent from './ExportResultDialog';

export class EventLog {
    constructor(public dateString: string, public eventString: string) { }
}

const CHART_NEED_PX = 196
const CHART_ANIM_MS = 550
const MIN_PICKER_PAD = 16
const CHART_HIDE_SLACK = 72
const CARD_FIT_GUTTER = 8
const MIDDLE_RESULTS_SPAN = 560

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: 'center',
        padding: '0 16px 8px',
        paddingBottom: 8,
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            padding: '0 8px 0',
            paddingBottom: 0,
        },
    },
    titleRow: {
        display: 'flex',
        flexShrink: 0,
        marginBottom: 8,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        minHeight: 48,
        position: 'relative',
        zIndex: 2,
        [theme.breakpoints.down('sm')]: {
            minHeight: 40,
            marginBottom: 4,
        },
    },
    roomName: {
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.15rem',
        },
    },
    titleActions: {
        display: 'flex',
        alignItems: 'center',
    },
    chart: {
        flexShrink: 0,
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'flex-start',
        overflow: 'hidden',
        marginTop: 4,
        width: '100%',
        height: 0,
        maxHeight: 0,
        opacity: 0,
        transition: 'height 0.5s ease, max-height 0.5s ease, opacity 0.5s ease',
    },
    chartOpen: {
        height: CHART_NEED_PX,
        maxHeight: CHART_NEED_PX,
        opacity: 1,
    },
    actions: {
        flexShrink: 0,
        position: 'relative',
        zIndex: 20,
        margin: '16px 0 8px',
        [theme.breakpoints.down('sm')]: {
            margin: '20px 0 8px',
        },
    },
    actionsResults: {
        margin: '16px 0 8px',
        [theme.breakpoints.down('sm')]: {
            margin: '20px 0 8px',
        },
    },
    timer: {
        flexShrink: 0,
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.15rem',
        },
    },
    spectators: {
        flexShrink: 0,
        marginBottom: 12,
        position: 'relative',
        zIndex: 6,
        [theme.breakpoints.down('sm')]: {
            marginBottom: 16,
            '& .MuiTypography-h6': {
                fontSize: '1rem',
            },
        },
    },
    playArea: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        position: 'relative',
        zIndex: 30,
    },
    table: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        overflow: 'visible',
        position: 'relative',
        zIndex: 8,
        transition: '--player-card-scale 0.5s ease, --player-card-w 0.5s ease, padding-top 0.5s ease',
    },
    playerCardItem: {
        minWidth: 0,
        flex: '0 0 auto',
        boxSizing: 'border-box',
    },
    picker: {
        flexShrink: 0,
        overflow: 'visible',
        maxHeight: 720,
        paddingTop: 44,
        paddingBottom: 'calc(18vh + 128px)',
        opacity: 1,
        transition: 'max-height 0.5s ease, padding 0.5s ease, opacity 0.5s ease',
        [theme.breakpoints.down('sm')]: {
            paddingTop: 40,
            maxHeight: 560,
        },
    },
    pickerCollapsed: {
        overflow: 'hidden',
        maxHeight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        opacity: 0,
        pointerEvents: 'none',
    },
    pickerFading: {
        opacity: 0,
        pointerEvents: 'none',
    },
}))

export default function RoomPlaying() {
    const classes = useStyles()
    const theme = useTheme()
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))
    const { roomState, roomProperties, previousRoomState, previousRoomProperties, player, makeEstimate, showCards, newGame, editRoom, setTimer, lockRoom, toggleSpectator, playerEmotes, performEmote, clearEmote } = useRoom()
    const [selectedValues, setSelectedValues] = useState<string[]>([])
    const [disableButton, setDisableButton] = useState(false)
    const [hideChart, setHideChart] = useState(false)
    const [hideChartForSpace, setHideChartForSpace] = useState(false)
    const [chartLeaving, setChartLeaving] = useState(false)
    const lastChartPlayersRef = useRef(roomState.players)
    const [hidePickCards, setHidePickCards] = useState(false)
    const [pickerSlotCollapsed, setPickerSlotCollapsed] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [emoteMenuAnchor, setEmoteMenuAnchor] = useState<null | HTMLElement>(null)
    const [showEdit, setShowEdit] = useState(false)
    const [showSetTimer, setShowSetTimer] = useState(false)
    const [showExportResults, setShowExportResults] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [eventLog, setEventLog] = useState<EventLog[]>([])
    const [linkSnackOpen, setLinkSnackOpen] = useState(false)
    const [zoomedCard, setZoomedCard] = useState("")
    const zoomedCardRef = useRef<HTMLDivElement>(null)
    const tableRef = useRef<HTMLDivElement>(null)
    const actionsRef = useRef<HTMLDivElement>(null)
    const playAreaRef = useRef<HTMLDivElement>(null)
    const pickerRef = useRef<HTMLDivElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const hideChartForSpaceRef = useRef(false)
    const layoutBaseRef = useRef(0)
    const lastPlayerCardScaleRef = useRef(1)
    const [playerCardScale, setPlayerCardScale] = useState(1)
    const [playerCardWidthPx, setPlayerCardWidthPx] = useState(128)
    const [playerCardTopPx, setPlayerCardTopPx] = useState(8)
    const [pickerBottomPx, setPickerBottomPx] = useState<number | null>(null)
    const [currentTime, setCurrentTime] = useState(Date.now())
    const [timerStartTimeOffset, setTimerStartTimeOffset] = useState<number | null>(null)
    const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null)

    // TODO - Replace this.  Cards aren't flipping because previous state is being updated to the current state before this is rendered
    const [hasShownInitialState, setHasShownInitialState] = useState(false)

    const menuOpen = Boolean(menuAnchor)
    const emoteMenuOpen = Boolean(emoteMenuAnchor)

    const [freeResponseText, setFreeResponseText] = useState([""])

    const isLocked = roomState.lockOwnerId !== null && roomState.lockOwnerId !== (player?.id ?? "")

    const emoteOptions = [
        "👍", "👎", "👏", "🤔",
        "🎯", "🔥", "❓", "⌛",
        "💯", "🚀", "😅", "👀",
        "😀", "💖", "🤯", "☕",
    ]

    function handleEditClicked() {
        handleMenuClose()
        setShowEdit(true)
    }

    function handleEditCancelled() {
        setShowEdit(false)
    }

    function handleEditSubmit(properties: RoomProperties) {
        editRoom(properties)
        setShowEdit(false)
    }

    function handleHistoryClicked() {
        handleMenuClose()
        setShowHistory(true)
    }

    function handleHistoryCancelled() {
        setShowHistory(false)
    }

    function handleCopyLinkClicked() {
        handleMenuClose()
        navigator.clipboard.writeText(window.location.href)
        setLinkSnackOpen(true)
    }

    function handleExportResultClicked() {
        setShowExportResults(true)
        handleMenuClose()
    }

    function handleExportResultCancelled() {
        setShowExportResults(false)
    }

    function handleLockClicked() {
        handleMenuClose()
        lockRoom()
    }

    function handleSpectateClicked() {
        handleMenuClose()
        toggleSpectator()
    }

    function handleSetTimerClicked() {
        handleMenuClose()
        setShowSetTimer(true)
    }

    function handleSetTimerSubmit(seconds: number) {
        setTimer(seconds)
        setShowSetTimer(false)
    }

    function handleSetTimerCancelled() {
        setShowSetTimer(false)
    }

    function handleLinkSnackClose() {
        setLinkSnackOpen(false)
    }

    function handleEmoteClicked(emote: string) {
        setEmoteMenuAnchor(null)
        performEmote(emote)
    }

    function logEvent(event: string) {
        let dateString = format(new Date(), "h:mm a")
        let item = new EventLog(dateString, event)
        let newLogList = eventLog
        newLogList.unshift(item)
        setEventLog(newLogList)
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (!(zoomedCardRef?.current?.contains(event.target as Node) ?? false)) {
            setZoomedCard("")
        }
    };

    useEffect(() => {
        // Initially hide the chart
        const showChart = roomProperties.gameType !== GameType.FreeResponse && roomState.status === roomStatuses.results
        setHideChart(!showChart)

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [])

    useLayoutEffect(() => {
        const el = tableRef.current
        const playArea = playAreaRef.current
        if (el == null || playArea == null) { return }

        let raf = 0
        let cancelled = false

        const updateScale = () => {
            const isHorizontal = roomProperties.gameType === GameType.FreeResponse
            const slotWidth = isHorizontal ? 240 : 128
            const slotHeight = isHorizontal ? 240 : 170
            const n = Math.max(1, el.querySelectorAll('.card-container-ingame').length)
            const tableW = el.clientWidth
            const preferredCols = Math.min(4, n)
            const extraCols = Math.max(0, Math.floor(tableW / slotWidth) - preferredCols)
            const cols = Math.min(n, preferredCols + extraCols)
            const rows = Math.max(1, Math.ceil(n / cols))
            const scaleW = Math.min(1, tableW / (cols * slotWidth))

            const chartEl = rootRef.current?.querySelector('.resultsChart') as HTMLElement | null
            const chartNow = chartEl != null ? chartEl.getBoundingClientRect().height : 0
            const measuredBase = playArea.clientHeight + chartNow
            const chartMoving = chartNow > 2 && chartNow < CHART_NEED_PX - 2
            if (!chartMoving || layoutBaseRef.current === 0) {
                layoutBaseRef.current = measuredBase
            }
            const base = layoutBaseRef.current

            const picker = pickerRef.current
            const pickerShown = picker != null && (roomState.status === roomStatuses.estimating || !pickerSlotCollapsed)
            const restingPadTop = isNarrow ? 40 : 44
            let pickerBody = 0
            let padTop = restingPadTop
            if (picker != null) {
                const inner = picker.firstElementChild instanceof HTMLElement ? picker.firstElementChild : null
                pickerBody = inner != null ? inner.offsetHeight : 0
                if (pickerBody < 40) {
                    pickerBody = 75
                }
                if (!pickerSlotCollapsed) {
                    padTop = parseFloat(getComputedStyle(picker).paddingTop) || restingPadTop
                }
            }

            const preferredPad = Math.round(window.innerHeight * 0.18 + 128)
            const solve = (playH: number, shown: boolean, minScale: number) => {
                const overhead = 8 + (shown ? pickerBody + padTop : 0)
                const space = playH - overhead - CARD_FIT_GUTTER
                const minPad = shown ? MIN_PICKER_PAD : 0
                let pad = shown ? preferredPad : 0
                let scale = scaleW
                const fullNeeded = rows * slotHeight * scale
                if (shown && fullNeeded + pad > space) {
                    pad = Math.max(minPad, space - fullNeeded)
                }
                const room = space - pad - rows * 24
                if (fullNeeded > room) {
                    scale = Math.min(scale, room / Math.max(1, rows * slotHeight))
                }
                scale = Math.max(minScale, scale)
                const fitsAtFloor = rows * slotHeight * 0.5 <= room
                return { scale, pad, fits: fitsAtFloor, preferred: preferredPad }
            }

            const chartEligible = roomState.status === roomStatuses.results
                && roomProperties.gameType !== GameType.FreeResponse
                && pickerSlotCollapsed

            let hideForSpace = false
            if (chartEligible) {
                const withChart = solve(base - CHART_NEED_PX, false, 0.5)
                const withSlack = solve(base - CHART_NEED_PX - CHART_HIDE_SLACK, false, 0.5)
                hideForSpace = hideChartForSpaceRef.current
                if (hideForSpace) {
                    if (withSlack.fits) { hideForSpace = false }
                } else if (!withChart.fits) {
                    hideForSpace = true
                }
            }

            const wantChart = chartEligible && !hideForSpace
            const chartBlock = wantChart ? CHART_NEED_PX : 0
            const layout = solve(base - chartBlock, pickerShown, 0.5)

            if (hideForSpace !== hideChartForSpaceRef.current) {
                hideChartForSpaceRef.current = hideForSpace
                setHideChartForSpace(hideForSpace)
            }

            if (Math.abs(layout.scale - lastPlayerCardScaleRef.current) >= 0.004) {
                lastPlayerCardScaleRef.current = layout.scale
                setPlayerCardWidthPx(Math.max(1, Math.round(slotWidth * layout.scale)))
                setPlayerCardScale(layout.scale)
            }

            const cardsH = rows * slotHeight * layout.scale
            const actionsEl = actionsRef.current
            const rootEl = rootRef.current
            let topPad = 0
            let tableTop = el.getBoundingClientRect().top
            let buttonBottom = actionsEl?.getBoundingClientRect().bottom ?? tableTop
            let screenBottom = rootEl?.getBoundingClientRect().bottom ?? tableTop
            if (!pickerShown && actionsEl != null && rootEl != null) {
                const chartDelta = chartBlock - chartNow
                buttonBottom += chartDelta
                screenBottom = rootEl.getBoundingClientRect().bottom
                tableTop += chartDelta
                const span = Math.max(screenBottom - buttonBottom, MIDDLE_RESULTS_SPAN)
                const anchorY = buttonBottom + 0.25 * span
                topPad = Math.round(Math.max(0, anchorY - tableTop - cardsH / 2))
            } else if (actionsEl != null) {
                const playBottom = playArea.getBoundingClientRect().bottom
                const pickerRest = pickerBody + padTop + layout.pad
                let pickerTop = playBottom - pickerRest - 28
                if (picker != null && !pickerSlotCollapsed) {
                    const live = picker.getBoundingClientRect()
                    if (live.height > 40) {
                        pickerTop = Math.min(pickerTop, live.top - 28)
                    }
                }
                const mid = (buttonBottom + pickerTop) / 2
                const centered = mid - tableTop - cardsH / 2
                const maxPad = pickerTop - tableTop - cardsH
                topPad = Math.round(Math.max(0, Math.min(centered, maxPad)))
            }
            if (!pickerShown) {
                const maxPad = Math.max(0, screenBottom - tableTop - cardsH - 8)
                topPad = Math.min(topPad, maxPad)
            }
            if (actionsEl != null) {
                topPad = Math.max(topPad, Math.round(buttonBottom + 16 - tableTop))
            }
            setPlayerCardTopPx(prev => prev === topPad ? prev : topPad)

            if (pickerShown) {
                const nextPad = layout.pad < layout.preferred - 1 ? Math.round(layout.pad) : null
                setPickerBottomPx(prev => prev === nextPad ? prev : nextPad)
            }
        }

        const scheduleScale = () => {
            if (raf !== 0) { return }
            raf = window.requestAnimationFrame(() => {
                raf = 0
                if (!cancelled) {
                    updateScale()
                }
            })
        }

        updateScale()
        window.addEventListener('resize', scheduleScale)
        if (typeof ResizeObserver === 'undefined') {
            return () => {
                cancelled = true
                window.removeEventListener('resize', scheduleScale)
            }
        }
        const observer = new ResizeObserver(scheduleScale)
        observer.observe(playArea)
        if (rootRef.current != null) {
            observer.observe(rootRef.current)
        }
        if (pickerRef.current != null) {
            observer.observe(pickerRef.current)
        }
        return () => {
            cancelled = true
            window.removeEventListener('resize', scheduleScale)
            observer.disconnect()
            if (raf !== 0) {
                window.cancelAnimationFrame(raf)
            }
        }
    }, [roomProperties.gameType, roomState.players.length, roomState.status, pickerSlotCollapsed, isNarrow])

    useEffect(() => {
        console.log("statestate")
        if (!hasShownInitialState || previousRoomState?.status !== roomState.status) {
            setHasShownInitialState(true)

            if (roomState.status === RoomStatus.Results && roomProperties.gameType !== GameType.FreeResponse) {
                let estimateString = roomState.players.filter(p => p.estimate.values.length > 0).map(p => (!roomProperties.anonymous ? (p.name + ": ") : "") + p.estimate.values[0]).join(", ")
                logEvent(estimateString)
            }

            flipCards(roomState.status !== roomStatuses.estimating)
            if (roomState.status === roomStatuses.estimating) {
                setChartLeaving(true)
                setTimeout(function () {
                    setHideChart(true)
                    setChartLeaving(false)
                }, CHART_ANIM_MS)
                setTimeout(function () {
                    setPickerSlotCollapsed(false)
                    setHidePickCards(false)
                }, 500)
            } else {
                setZoomedCard("")
                setChartLeaving(false)
                setHidePickCards(true)
                setTimeout(function () {
                    setPickerSlotCollapsed(true)
                    setHideChart(false)
                }, 500)
            }

            setTimeout(function () {
                if (roomState.status === roomStatuses.results) {
                    resetUserInput()
                    anime({
                        targets: ".actionButton",
                        opacity: 1,
                        easing: 'easeInOutSine',
                        duration: CHART_ANIM_MS
                    })
                }
            }, 80)

            setDisableButton(true)
            setTimeout(function () {
                setDisableButton(false)
            }, 1200)
        }
        if (!hasShownInitialState || roomState.timer !== previousRoomState?.timer) {
            if (timerInterval != null) {
                clearInterval(timerInterval)
                setTimerInterval(null)
            }

            if (roomState.timer == null) {
                setTimerStartTimeOffset(null)
            } else {
                if (timerStartTimeOffset == null) {
                    var offset = Date.now() - roomState.timer.serverStartTimeMillis
                    setTimerStartTimeOffset(offset)
                }
                setTimerInterval(setInterval(() => updateTime(), 200))
            }
        }
    }, [roomState])

    useEffect(() => {
        if (previousRoomProperties?.gameType !== roomProperties.gameType) {
            resetUserInput()
        }
    }, [roomProperties])

    function resetUserInput() {
        setSelectedValues([])
        setFreeResponseText([""])
    }

    function flipCards(isFront: boolean) {
        console.log("Flipping cards: " + isFront + " " + roomProperties.gameType)
        let isHorizontal = roomProperties.gameType === GameType.FreeResponse
        anime({
            targets: ".poker-card-game", // Only animate actual game cards
            scale: [{ value: 1 }, { value: 1.05 }, { value: 1, delay: 150 }],
            rotateY: { value: isFront && !isHorizontal ? 180 : 0 },
            rotateX: { value: isFront && isHorizontal ? 180 : 0 },
            easing: 'easeInOutSine',
            delay: anime.stagger(100, { from: 'center' }, { easing: 'easeOutQuad' }),
            duration: 400
        })
        if (hasShownInitialState) {
            notifyCardsFlipped(isFront)
        }
    }

    function handleValueSelected(value: string | null) {
        const firstValue = selectedValues.length > 0 ? selectedValues[0] : ""
        const newValue = (value !== firstValue ? value : "") ?? ""
        setSelectedValues([newValue])
        makeEstimate(new Estimate(player!.id, [newValue]))
    }

    function onShowCardsPressed() {
        showCards()
    }

    function onNewGamePressed() {
        newGame()
    }

    function updateTime() {
        setCurrentTime(Date.now())
    }

    function handleFreeResponseReadyClicked() {
        let newSelectedValues = selectedValues.length === 0 ? freeResponseText.filter(v => v.length > 0) : []
        setSelectedValues(newSelectedValues)
        makeEstimate(new Estimate(player!.id, newSelectedValues))
    }

    function handleMenuOpen(event: React.MouseEvent<HTMLButtonElement>) {
        setMenuAnchor(event.currentTarget);
    }

    function handleMenuClose() {
        setMenuAnchor(null);
    }

    function renderActionButton() {
        if (roomState.status === roomStatuses.estimating) {
            return <Button
                className="actionButton"
                variant="contained"
                color="primary"
                disabled={isLocked || !roomState.players.some(player => player.hasEstimated)}
                onClick={onShowCardsPressed}>
                Show Cards
            </Button>
        } else {
            return <Button variant="contained" color="primary" className="actionButton" onClick={onNewGamePressed} disabled={isLocked || disableButton}> New Game </Button>
        }
    }

    function renderTimer() {
        var timer = roomState.timer
        if (timer == null) { return null }
        var secondsRemaining = (timer.serverStartTimeMillis + (timerStartTimeOffset ?? 0) + timer.totalTimeMillis - currentTime) / 1000
        // Because we add 1 it's possible for it to briefly show as over the total time, don't display any higher than total time.
        secondsRemaining = Math.min(secondsRemaining + 1, timer.totalTimeMillis / 1000)

        if (secondsRemaining < 0) { return null }

        var minutes = Math.floor(secondsRemaining / 60)
        var seconds = (Math.floor(secondsRemaining) % 60)
        var secondsString = minutes > 0 && seconds < 10 ? "0" + seconds : seconds
        var displayString = minutes > 0 ? `${minutes} : ${secondsString}` : secondsString
        return displayString
    }

    let spectators = roomState.players.filter(p => p.isSpectator)
    let spectatorListText = roomProperties.anonymous ? ("(" + spectators.length + ")") : null
    const timerDisplay = renderTimer()
    const isResultsView = roomState.status === roomStatuses.results
    const hasChartEstimates = roomState.players.filter(player => player.estimate.values.length > 0).length > 0
    if (isResultsView && hasChartEstimates) {
        lastChartPlayersRef.current = roomState.players
    }
    const chartPlayers = lastChartPlayersRef.current
    const showResultsChart = roomProperties.gameType !== GameType.FreeResponse && !hideChart && (isResultsView || chartLeaving) && chartPlayers.some(player => player.estimate.values.length > 0)
    const chartOpen = showResultsChart && isResultsView && !chartLeaving && !hideChartForSpace
    const pickerStyle: React.CSSProperties | undefined = pickerSlotCollapsed
        ? { paddingBottom: 0 }
        : (pickerBottomPx != null ? { paddingBottom: pickerBottomPx } : undefined)

    return (
        <div className={classes.root} ref={rootRef}>
            <div className={classes.titleRow}>
                <Typography variant="h5" className={classes.roomName}>
                    {roomProperties.name}
                </Typography>
                <div className={classes.titleActions}>
                    <IconButton color="inherit" onClick={handleMenuOpen} size={isNarrow ? "small" : "medium"}>
                        <MoreVertIcon />
                    </IconButton>
                    {!roomProperties.disableEmotes && !(player?.isSpectator ?? false) ?
                        <IconButton color="inherit" onClick={(e) => setEmoteMenuAnchor(e.currentTarget)} size={isNarrow ? "small" : "medium"}>
                            <EmoteIcon />
                        </IconButton> : <></>
                    }
                </div>
            </div>

            <Box className={`resultsChart ${classes.chart} ${chartOpen ? classes.chartOpen : ''}`}>
                {showResultsChart ?
                    <ResultsChart players={chartPlayers} animate={isResultsView && !chartLeaving} /> : <></>
                }
            </Box>

            <div ref={actionsRef} className={`${classes.actions} ${isResultsView ? classes.actionsResults : ''}`}>
                {renderActionButton()}
            </div>

            {timerDisplay ?
                <Typography variant="h5" className={classes.timer}>
                    {timerDisplay}
                </Typography> : null
            }

            {spectators.length > 0 ?
                <div className={classes.spectators}>
                    <Typography variant="h6">Spectators: </Typography>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Typography variant="body1">
                            {spectatorListText != null ? spectatorListText : spectators.map((s, i) => (
                                <span key={s.id} style={{ textDecoration: s.disconnected ? 'line-through' : undefined }}>
                                    {s.name}{i < spectators.length - 1 ? ', ' : ''}
                                </span>
                            ))}
                        </Typography>
                    </div>
                </div> : <></>
            }

            <div ref={playAreaRef} className={classes.playArea}>
            <div className={classes.table}
                ref={tableRef}
                style={{
                    ['--player-card-scale' as string]: playerCardScale,
                    ['--player-card-w' as string]: `${playerCardWidthPx}px`,
                    paddingTop: playerCardTopPx,
                }}>
                <Grid container alignItems="center" justifyContent="center">
                    {roomState.players.filter(player => !player.isSpectator).map(player => (
                        <React.Fragment key={`poker-card-player-div-${player.id}`} >
                            {(player.estimate.values.length === 0 ? [""] : player.estimate.values).map((estimate: string, index: number) =>
                                <Grid ref={zoomedCard === `${player.id}${index}` ? zoomedCardRef : null} key={`grid-card-${player.id}-${index}`} item
                                    className={classes.playerCardItem}
                                    style={{ zIndex: zoomedCard === `${player.id}${index}` ? 10 : 5 }}>
                                    <PokerCard
                                        key={`poker-card-${index}`}
                                        selectKey={`${player.id}${index}`} value={estimate} hasEstimated={player.hasEstimated} name={player.name}
                                        inGame={true} isAnonymous={roomProperties.anonymous} isHorizontal={roomProperties.gameType === GameType.FreeResponse}
                                        emote={index === 0 ? (playerEmotes.get(player.id) ?? "") : ""}
                                        onEmoteFinished={function (): void { clearEmote(player.id) }} invertFlip={false}
                                        isZoomed={zoomedCard === `${player.id}${index}`}
                                        isDisconnected={player.disconnected}
                                        image={null} isSelected={false} onClick={(key: string) => {
                                            if (roomState.status === RoomStatus.Results) {
                                                setZoomedCard(`${player.id}${index}`)
                                            }
                                        }} />
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                </Grid>
            </div>

            {!(player?.isSpectator ?? false) && roomProperties.gameType == GameType.PickCards ?
                <div ref={pickerRef} style={pickerStyle} className={`${classes.picker} ${!hidePickCards && roomState.status === roomStatuses.estimating ? '' : classes.pickerFading} ${pickerSlotCollapsed ? classes.pickerCollapsed : ''}`}>
                    <CardPicker
                        possibleCards={roomProperties.votingScheme}
                        selectedValue={selectedValues.length > 0 ? selectedValues[0] : ""}
                        roomStatus={roomState.status}
                        handleValueSelected={handleValueSelected} />
                </div>
                : null
            }

            {!(player?.isSpectator ?? false) && roomProperties.gameType == GameType.FreeResponse ?
                <div ref={pickerRef} style={pickerStyle} className={`${classes.picker} ${!hidePickCards && roomState.status === roomStatuses.estimating ? '' : classes.pickerFading} ${pickerSlotCollapsed ? classes.pickerCollapsed : ''}`}>
                    <FreeResponseInput
                        freeResponseText={freeResponseText}
                        selectedValues={selectedValues}
                        handleReadyClicked={handleFreeResponseReadyClicked}
                        handleUpdateClicked={() => {
                            setSelectedValues(freeResponseText)
                            makeEstimate(new Estimate(player!.id, freeResponseText))
                        }}
                        setFreeResponseText={setFreeResponseText}
                        makeEstimate={makeEstimate}
                    />
                </div>
                : null
            }
            </div>

            <Dialog fullWidth={true} open={showEdit} onClose={handleEditCancelled} aria-labelledby="form-dialog-title">
                <CreateDialogContent onCancel={handleEditCancelled} onCreate={handleEditSubmit} existingProperties={roomProperties} />
            </Dialog>

            <Dialog fullWidth={true} open={showHistory} onClose={handleHistoryCancelled} aria-labelledby="form-dialog-title">
                <EventHistoryDialogContent events={eventLog} />
            </Dialog>

            <Dialog fullWidth={true} open={showSetTimer} onClose={handleSetTimerCancelled} aria-labelledby="form-dialog-title">
                    <SetTimerDialogContent onCancel={handleSetTimerCancelled} onStart={handleSetTimerSubmit} />
                </Dialog>

            <Dialog fullWidth={true} open={showExportResults} onClose={handleExportResultCancelled} aria-labelledby="form-dialog-title">
                <ExportResultsDialogContent players={roomState.players} onCancel={handleExportResultCancelled} />
             </Dialog>

            <Snackbar open={linkSnackOpen} autoHideDuration={6000} onClose={handleLinkSnackClose}>
                <SnackbarContent message={"Link copied!"} />
            </Snackbar>

            <Menu
                id="room-menu"
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}>

                <MenuItem onClick={handleEditClicked} disabled={isLocked} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit Room</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSetTimerClicked} disabled={isLocked || roomState.status == RoomStatus.Results} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        <TimerIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText> Set Timer </ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLockClicked} disabled={isLocked} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        {roomState.lockOwnerId === null ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText> {roomState.lockOwnerId === null ? "Lock Controls" : "Unlock Controls"}</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem onClick={handleExportResultClicked} disabled = {roomState.status != RoomStatus.Results} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        <FileCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Export Result</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleHistoryClicked} disabled={eventLog.length === 0} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>History</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleCopyLinkClicked} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        <ShareIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy Link</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem onClick={handleSpectateClicked} style={{ display: 'flex', justifyContent: "space-between", width: "200px" }}>
                    <ListItemIcon>
                        {player?.isSpectator ?? false ? <GroupAddIcon fontSize="small" /> : <GroupIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText> {player?.isSpectator ?? false ? "Join" : "Spectate"}</ListItemText>
                </MenuItem>
            </Menu>

            <Menu
                id="emote-menu"
                anchorEl={emoteMenuAnchor}
                open={emoteMenuOpen}
                onClose={() => setEmoteMenuAnchor(null)}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}>

                <Grid container spacing={0} style={{ width: "240px" }}>
                    {emoteOptions.map((emote, index) =>
                        <Grid item xs={3} style={{ textAlign: "center" }} key={`emote-icon-${index}`}>
                            <IconButton color="inherit" key={`emote-icon-button-${index}`} onClick={(e) => handleEmoteClicked(emote)} style={{ fontSize: "24px", textAlign: "center" }}>
                                {emote}
                            </IconButton>
                        </Grid>
                    )}
                </Grid>

            </Menu>

        </div>
    )
}
